import React, { useMemo, useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Download } from 'lucide-react';
import CarbonIntensityLookup from './CarbonIntensityLookup';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const PVROIAnalysis = () => {
  // PV ROI inputs
  const [pvInputs, setPvInputs] = useState({
    panelEmbodied: '', // kg CO2-eq
    otherEmbodied: '', // kg CO2-eq (inverter, supports)
    panelEfficiency: '', // %
    annualSolar: '', // e.g., kWh/m^2/year
    roofCoverage: '', // % roof coverage
    degradation: '', // %/year
    otherIncludedAbove: false, // whether other components are included in panel embodied carbon
    pvServiceLife: 25, // years (25-35)
    gridIntensity: '' // kg CO2-eq/kWh
  });

  // Edit mode for PV service life
  const [isEditingServiceLife, setIsEditingServiceLife] = useState(false);
  const [tempServiceLife, setTempServiceLife] = useState(25);

  // Starting year for the analysis
  const [startingYear, setStartingYear] = useState(new Date().getFullYear());

  // GI row editing state
  const [isEditingGI, setIsEditingGI] = useState(false);
  const [tempGIRow, setTempGIRow] = useState({});
  const [savedGIRow, setSavedGIRow] = useState({});
  const [useCustomGI, setUseCustomGI] = useState(false);
  const [showGridLookup, setShowGridLookup] = useState(false);
  const [lastLookupSource, setLastLookupSource] = useState(null);
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Refs for precise export targeting
  const chartContainerRef = useRef(null);
  const svgRef = useRef(null);

  const handlePvChange = (field, value) => {
    if (field === 'pvServiceLife') {
      const numValue = parseInt(value);
      if (numValue < 25 || numValue > 35) {
        setShowValidationAlert(true);
        return;
      }
    }
    setPvInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleEditServiceLife = () => {
    setTempServiceLife(pvInputs.pvServiceLife);
    setIsEditingServiceLife(true);
  };

  const handleDoneServiceLife = () => {
    if (tempServiceLife < 25 || tempServiceLife > 35) {
      setShowValidationAlert(true);
      return;
    }
    setPvInputs(prev => ({ ...prev, pvServiceLife: tempServiceLife }));
    setIsEditingServiceLife(false);
  };

  const handleCancelServiceLife = () => {
    setTempServiceLife(pvInputs.pvServiceLife);
    setIsEditingServiceLife(false);
  };

  const createDefaultGIRow = () => {
    const serviceLife = parseInt(pvInputs.pvServiceLife) || 25;
    const defaultValue = pvInputs.gridIntensity || '';
    const row = {};
    for (let i = 0; i <= serviceLife; i++) {
      if (savedGIRow[i] !== undefined) {
        row[i] = savedGIRow[i];
      } else {
        row[i] = defaultValue;
      }
    }
    return row;
  };

  const handleEditGI = () => {
    const initialGIRow = createDefaultGIRow();
    setTempGIRow(initialGIRow);
    setIsEditingGI(true);
  };

  const handleDoneGI = () => {
    // Save the individual cell values and lock GI input
    setSavedGIRow({ ...tempGIRow });
    setIsEditingGI(false);
    setUseCustomGI(true);
  };

  const handleCancelGI = () => {
    setIsEditingGI(false);
  };

  const handleGICellChange = (colIndex, value) => {
    setTempGIRow(prev => ({ ...prev, [colIndex]: value }));
  };

  const handleNoDecarbonization = () => {
    setUseCustomGI(false);
    setSavedGIRow({});
    setIsEditingGI(false);
    setTempGIRow({});
  };

  const handleToggleDecarbonize = () => {
    const active = isEditingGI || useCustomGI;
    if (active) {
      const shouldRevert = typeof window === 'undefined'
        ? true
        : window.confirm('Are you sure you want to revert? Modified GI values will be discarded');
      if (shouldRevert) {
        handleNoDecarbonization();
      }
      return;
    }

    const defaultRow = createDefaultGIRow();
    setSavedGIRow(defaultRow);
    setUseCustomGI(true);
  };

  const handleApplyLookupValue = ({ country, year, valueKg }) => {
    if (typeof valueKg !== 'number' || Number.isNaN(valueKg)) {
      return;
    }
    const rounded = Number(valueKg.toFixed(3));
    setPvInputs(prev => ({ ...prev, gridIntensity: rounded.toFixed(3) }));
    setUseCustomGI(false);
    setSavedGIRow({});
    setIsEditingGI(false);
    setShowGridLookup(false);
    const countryName = country?.name || 'dataset';
    const countryCode = country?.code || '';
    setLastLookupSource({
      countryName,
      countryCode,
      year: year || null,
      valueKg: rounded,
    });
  };

  // Auto-hide validation alert after 5 seconds
  useEffect(() => {
    if (showValidationAlert) {
      const timer = setTimeout(() => {
        setShowValidationAlert(false);
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [showValidationAlert]);

  // Chart positioning configuration for PDF export
  const CHART_EXPORT_CONFIG = {
    verticalOffset: 180,    // Move chart down by this many pixels (increase = more down)
    horizontalOffset: 70,    // Move chart left/right by this many pixels (positive = right, negative = left)
    horizontalCenter: false  // Set to false if you want manual horizontal positioning
  };

  // Function to download the chart as PDF
  const downloadChartAsPDF = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default || html2canvasModule.html2canvas || html2canvasModule;

      const panelToCapture = chartContainerRef.current;
      const svgElement = svgRef.current;
      if (!panelToCapture) throw new Error('Chart container not found');

      // Hide the in-chart overlay so it doesn't cover the chart during capture
      const overlayEl = panelToCapture.querySelector('[data-pdf-overlay="true"]');
      const prevOverlayDisplay = overlayEl ? overlayEl.style.display : null;
      if (overlayEl) overlayEl.style.display = 'none';

      // Allow the DOM to update after hiding the overlay
      await new Promise(resolve => setTimeout(resolve, 50));

      let canvas = await html2canvas(panelToCapture, {
        backgroundColor: '#ffffff',
        scale: Math.min(2, window.devicePixelRatio || 1),
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        foreignObjectRendering: false,
        removeContainer: true,
        ignoreElements: (el) => el?.getAttribute?.('data-pdf-overlay') === 'true'
      });

      // Check if canvas is blank (all white)
      const ctx = canvas.getContext('2d');
      const sample = ctx.getImageData(0, 0, Math.min(10, canvas.width), Math.min(10, canvas.height)).data;
      let isBlank = true;
      for (let i = 0; i < sample.length; i += 4) {
        if (sample[i] !== 255 || sample[i + 1] !== 255 || sample[i + 2] !== 255) { isBlank = false; break; }
      }

      // Fallback to direct SVG rasterization if needed
      if (isBlank && svgElement) {
        const serialized = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([serialized], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
        });
        const fallbackCanvas = document.createElement('canvas');
        const svgW = svgElement.getAttribute('width') ? parseFloat(svgElement.getAttribute('width')) : (svgElement.clientWidth || 1200);
        const svgH = svgElement.getAttribute('height') ? parseFloat(svgElement.getAttribute('height')) : (svgElement.clientHeight || 400);
        fallbackCanvas.width = Math.max(1, Math.floor(svgW));
        fallbackCanvas.height = Math.max(1, Math.floor(svgH));
        const fctx = fallbackCanvas.getContext('2d');
        fctx.fillStyle = '#ffffff';
        fctx.fillRect(0, 0, fallbackCanvas.width, fallbackCanvas.height);
        fctx.drawImage(img, 0, 0, fallbackCanvas.width, fallbackCanvas.height);
        URL.revokeObjectURL(url);
        canvas = fallbackCanvas;
      }

      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - margin * 2;
      const scaleX = availableWidth / canvas.width;
      const scaleY = availableHeight / canvas.height;
      const scale = Math.min(scaleX, scaleY);
      const finalWidth = canvas.width * scale;
      const finalHeight = canvas.height * scale;
      const x = (pageWidth - finalWidth) / 2;
      const y = (pageHeight - finalHeight) / 2;
      const imgData = canvas.toDataURL('image/png', 1.0);
      pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
      const filename = `PV_ROI_Analysis_Chart_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

      // Restore overlay visibility
      if (overlayEl) overlayEl.style.display = prevOverlayDisplay;
    } catch (error) {
      alert(`PDF generation failed: ${error.message}. Please try again.`);
    } finally {
      setIsDownloading(false);
    }
  };

  const pvComputed = useMemo(() => {
    const panelEmbodied = parseFloat(pvInputs.panelEmbodied) || 0;
    const otherEmbodied = pvInputs.otherIncludedAbove ? 0 : (parseFloat(pvInputs.otherEmbodied) || 0);
    const efficiencyPct = parseFloat(pvInputs.panelEfficiency) || 0; // %
    const annualSolar = parseFloat(pvInputs.annualSolar) || 0; // units per m^2
    const roofCoveragePct = parseFloat(pvInputs.roofCoverage) || 0; // %

    const totalEmbodied = panelEmbodied + otherEmbodied; // kg CO2-eq
    const annualElectricity = annualSolar * (roofCoveragePct / 100) * (efficiencyPct / 100); // generic units

    return { totalEmbodied, annualElectricity };
  }, [pvInputs]);

  // Prepare series for the PV ROI chart
  const roiSeries = useMemo(() => {
    const numCols = (parseInt(pvInputs.pvServiceLife) || 25) + 1;
    const giDefault = parseFloat(pvInputs.gridIntensity) || 0;
    const degradationPctPerYear = parseFloat(pvInputs.degradation) || 0; // % per year
    const annualElectricity = pvComputed.annualElectricity || 0;
    const embodied0 = pvComputed.totalEmbodied || 0;

    const years = Array.from({ length: numCols }, (_, i) => startingYear + i);

    const performancePct = Array.from({ length: numCols }, (_, i) => {
      const value = Math.max(0, 100 - degradationPctPerYear * i);
      return value; // keep as percent
    });

    const giByYear = Array.from({ length: numCols }, (_, i) => {
      if (useCustomGI) {
        const v = parseFloat(savedGIRow[i]);
        return isNaN(v) ? giDefault : v;
      }
      return giDefault;
    });

    const operationalSavings = Array.from({ length: numCols }, (_, i) => {
      if (i === 0) return 0;
      const perf = performancePct[i] / 100; // to fraction
      const val = -(giByYear[i] * perf * annualElectricity);
      return parseFloat(val.toFixed(2));
    });

    const embodied = Array.from({ length: numCols }, (_, i) => (i === 0 ? embodied0 : 0));

    const net = Array(numCols).fill(0);
    net[0] = embodied0;
    for (let i = 1; i < numCols; i++) {
      net[i] = parseFloat((net[i - 1] + operationalSavings[i]).toFixed(1));
    }

    // Scale domain should be based on bars only (embodied and savings),
    // not forced to include full net range. The net line will clip at chart edge.
    const minY = Math.min(0, ...operationalSavings);
    const maxY = Math.max(0, embodied0);

    return { years, embodied, operationalSavings, net, minY, maxY };
  }, [pvInputs, savedGIRow, useCustomGI, pvComputed, startingYear]);

  const hasRoiData = useMemo(() => {
    const panelEmbodied = parseFloat(pvInputs.panelEmbodied);
    const panelEfficiency = parseFloat(pvInputs.panelEfficiency);
    const annualSolar = parseFloat(pvInputs.annualSolar);
    const roofCoverage = parseFloat(pvInputs.roofCoverage);
    const defaultGI = parseFloat(pvInputs.gridIntensity);

    const hasCustomGIValues = useCustomGI && Object.values(savedGIRow).some((value) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) && parsed > 0;
    });

    const hasGridIntensity = (Number.isFinite(defaultGI) && defaultGI > 0) || hasCustomGIValues;

    return (
      Number.isFinite(panelEmbodied) && panelEmbodied > 0 &&
      Number.isFinite(panelEfficiency) && panelEfficiency > 0 &&
      Number.isFinite(annualSolar) && annualSolar > 0 &&
      Number.isFinite(roofCoverage) && roofCoverage > 0 &&
      hasGridIntensity
    );
  }, [pvInputs, savedGIRow, useCustomGI]);

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-emerald-100 border rounded-xl shadow-lg">
      <CardHeader className="text-center">
        <CardTitle className="text-gray-800">PV Carbon ROI Analysis</CardTitle>
        <CardDescription>
          Enter PV system details and context to estimate production, break-even year and carbon savings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* PV Panel Inputs */}
          <div className="space-y-3 border border-gray-200 rounded-lg p-4 bg-gray-50/50">
            <h4 className="font-semibold text-center text-gray-800 mb-8">PV Panel & System</h4>
            <div className="border-b border-gray-300 w-full"></div>
            <div>
              <Label htmlFor="pv-panel-embodied">PV Panel embodied carbon (kgCO2-eq)</Label>
              <Input id="pv-panel-embodied" type="number" value={pvInputs.panelEmbodied} onChange={(e)=>handlePvChange('panelEmbodied', e.target.value)} className="mt-1" placeholder="e.g., 12000" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="pv-other-embodied">Other components - inverter/support (kgCO2-eq)</Label>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="other-included-above"
                    checked={pvInputs.otherIncludedAbove}
                    onChange={(e) => handlePvChange('otherIncludedAbove', e.target.checked)}
                    className="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2"
                  />
                  <Label htmlFor="other-included-above" className="text-sm text-gray-600">Included in the above option</Label>
                </div>
              </div>
              <Input 
                id="pv-other-embodied" 
                type="number" 
                value={pvInputs.otherEmbodied} 
                onChange={(e)=>handlePvChange('otherEmbodied', e.target.value)} 
                className="mt-1" 
                placeholder="e.g., 2000"
                disabled={pvInputs.otherIncludedAbove}
              />
            </div>
            <div>
              <Label htmlFor="pv-efficiency">PV panel efficiency (%)</Label>
              <Input id="pv-efficiency" type="number" value={pvInputs.panelEfficiency} onChange={(e)=>handlePvChange('panelEfficiency', e.target.value)} className="mt-1" placeholder="e.g., 18" />
            </div>
            <div>
              <Label className="group relative cursor-help">
                Total PV system embodied carbon
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-white border border-gray-300 rounded px-2 py-1 text-xs font-medium text-gray-700 whitespace-nowrap shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                  PV Panel embodied carbon + Other components
                </div>
              </Label>
              <div className="mt-1 text-sm font-semibold text-gray-900">
                {pvComputed.totalEmbodied.toLocaleString()} kgCO2-eq
              </div>
            </div>
          </div>

          {/* Context Inputs */}
          <div className="space-y-3 border border-gray-200 rounded-lg p-4 bg-gray-50/50">
            <h4 className="font-semibold text-center text-gray-800 mb-8">Context</h4>
            <div className="border-b border-gray-300 w-full"></div>
            <div>
              <Label htmlFor="pv-annual-solar">Annual solar radiation - avg (kWh/m^2/year)</Label>
              <Input id="pv-annual-solar" type="number" value={pvInputs.annualSolar} onChange={(e)=>handlePvChange('annualSolar', e.target.value)} className="mt-1" placeholder="e.g., 1700" />
            </div>
            <div>
              <Label htmlFor="pv-roof-coverage">Panel roof coverage (%)</Label>
              <Input id="pv-roof-coverage" type="number" value={pvInputs.roofCoverage} onChange={(e)=>handlePvChange('roofCoverage', e.target.value)} className="mt-1" placeholder="e.g., 80" />
            </div>
            <div>
              <Label htmlFor="pv-degradation">Degradation factor (%/year)</Label>
              <Input id="pv-degradation" type="number" value={pvInputs.degradation} onChange={(e)=>handlePvChange('degradation', e.target.value)} className="mt-1" placeholder="e.g., 0.8" />
            </div>
            <div>
              <Label className="group relative cursor-help">
                Annual electricity production
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-white border border-gray-300 rounded px-2 py-1 text-xs font-medium text-gray-700 whitespace-nowrap shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                  Annual solar radiation * (panel roof coverage %) * (PV panel efficiency %)
                </div>
              </Label>
              <div className="mt-1 text-sm font-semibold text-gray-900">
                {pvComputed.annualElectricity.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 1 })} kWh/m^2 PV
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Info Section */}
        <div className="mt-6 border border-gray-200 rounded-lg p-4 bg-gray-50/50">
          <h4 className="font-semibold text-center text-gray-800 mb-4">Analysis Info</h4>
          <div className="border-b border-gray-300 w-full mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pv-service-life">PV service life (years)</Label>
              <div className="flex items-center space-x-2 mt-1">
                <Input 
                  id="pv-service-life" 
                  type="number" 
                  min="25" 
                  max="35" 
                  value={isEditingServiceLife ? tempServiceLife : pvInputs.pvServiceLife} 
                  onChange={(e) => isEditingServiceLife ? setTempServiceLife(parseInt(e.target.value) || 25) : handlePvChange('pvServiceLife', parseInt(e.target.value) || 25)} 
                  className="flex-1" 
                  placeholder="25"
                  disabled={!isEditingServiceLife}
                />
                {!isEditingServiceLife ? (
                  <button
                    onClick={handleEditServiceLife}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    title="Edit service life"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                ) : (
                  <div className="flex space-x-1">
                    <button
                      onClick={handleDoneServiceLife}
                      className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                      title="Apply changes"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleCancelServiceLife}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                      title="Cancel changes"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="grid-intensity">Grid intensity - GI (kg/kWh)</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={handleToggleDecarbonize}
                      aria-pressed={isEditingGI || useCustomGI}
                      className={`px-3 py-1 text-sm rounded-md border transition-colors ${
                        isEditingGI || useCustomGI
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Decarbonize
                    </button>
                    {(useCustomGI || isEditingGI) && !isEditingGI && (
                      <button
                        type="button"
                        onClick={handleEditGI}
                        className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-md transition-colors"
                      >
                        Edit schedule
                      </button>
                    )}
                    {isEditingGI && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={handleDoneGI}
                          className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                          title="Apply GI changes"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={handleCancelGI}
                          className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                          title="Cancel GI changes"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setShowGridLookup(true)}
                    className="px-3 py-1 text-sm bg-sky-100 text-sky-700 hover:bg-sky-200 rounded-md transition-colors"
                  >
                    Lookup grid carbon
                  </button>
                </div>
              </div>
              <Input 
                id="grid-intensity" 
                type="number" 
                value={pvInputs.gridIntensity} 
                onChange={(e)=>handlePvChange('gridIntensity', e.target.value)} 
                className="mt-1" 
                placeholder="e.g., 0.4"
                disabled={isEditingGI || useCustomGI}
              />
              {lastLookupSource && typeof lastLookupSource.valueKg === 'number' && (
                <p className="mt-1 text-xs text-gray-500">
                  {`Last applied from ${lastLookupSource.countryName}${lastLookupSource.countryCode ? ` (${lastLookupSource.countryCode})` : ''}${lastLookupSource.year ? ` - ${lastLookupSource.year}` : ''}: ${lastLookupSource.valueKg.toFixed(3)} kgCO2/kWh`}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Analysis Table */}
        <div className="mt-6">
          <h4 className="font-semibold text-gray-800 mb-4">Analysis Results</h4>
          <div className="overflow-x-auto">
            {!hasRoiData ? (
              <div className="flex min-h-[160px] items-center justify-center rounded-lg border border-dashed border-emerald-200 bg-white/80 px-6 text-center text-sm text-gray-600">
                Enter the details under 'PV Panel & System' and 'Context' to compute
              </div>
            ) : (
              <table className="w-full border-collapse border border-gray-300 bg-white rounded-lg">
                <thead>
                  <tr>
                    <th className="border border-gray-300 px-2 py-1 text-left font-semibold bg-gray-100 text-xs">Lifespan</th>
                    {Array.from({ length: pvInputs.pvServiceLife + 1 }, (_, i) => (
                      <th key={i} className="border border-gray-300 px-1 py-1 text-center text-xs">
                        {i}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 6 }, (_, rowIndex) => (
                    <tr key={rowIndex}>
                      <td className="border border-gray-300 px-2 py-1 font-semibold bg-gray-100 text-xs h-8 whitespace-nowrap">
                        {rowIndex === 0 ? 'Years' : rowIndex === 1 ? 'GI' : rowIndex === 2 ? 'Degradation performance' : rowIndex === 3 ? 'Embodied Carbon' : rowIndex === 4 ? 'Operational savings' : 'Net'}
                      </td>
                      {Array.from({ length: pvInputs.pvServiceLife + 1 }, (_, colIndex) => (
                        <td key={colIndex} className="border border-gray-300 px-1 py-1 text-center text-xs h-8 w-12">
                          {rowIndex === 0 ? (
                            colIndex === 0 ? (
                              <input
                                type="text"
                                value={startingYear}
                                onChange={(e) => setStartingYear(parseInt(e.target.value) || new Date().getFullYear())}
                                className="w-full text-center bg-transparent border-none outline-none text-xs"
                                min="1900"
                                max="2100"
                              />
                            ) : (
                              startingYear + colIndex
                            )
                          ) : rowIndex === 1 ? (
                            isEditingGI ? (
                              <input
                                type="text"
                                value={tempGIRow[colIndex] || ''}
                                onChange={(e) => handleGICellChange(colIndex, e.target.value)}
                                className="w-8 text-center bg-transparent border-none outline-none text-xs"
                                placeholder="0.4"
                              />
                            ) : useCustomGI ? (
                              (() => {
                                const savedValue = savedGIRow[colIndex];
                                if (savedValue !== undefined && savedValue !== null && savedValue !== '') {
                                  return savedValue;
                                }
                                if (pvInputs.gridIntensity !== undefined && pvInputs.gridIntensity !== null && pvInputs.gridIntensity !== '') {
                                  return pvInputs.gridIntensity;
                                }
                                return '-';
                              })()
                            ) : (
                              pvInputs.gridIntensity || '-'
                            )
                          ) : rowIndex === 2 ? (
                            (() => {
                              const degradationFactor = parseFloat(pvInputs.degradation) || 0;
                              let performance = 100;
                              for (let i = 0; i < colIndex; i++) {
                                performance -= degradationFactor;
                              }
                              return Math.max(0, performance).toFixed(1);
                            })()
                          ) : rowIndex === 3 ? (
                            colIndex === 0 ? pvComputed.totalEmbodied.toLocaleString() : '-'
                          ) : rowIndex === 4 ? (
                            colIndex === 0 ? '0' : (() => {
                              const gi = useCustomGI ? (parseFloat(savedGIRow[colIndex]) || 0) : (parseFloat(pvInputs.gridIntensity) || 0);
                              const degradationFactor = parseFloat(pvInputs.degradation) || 0;
                              let performance = 100;
                              for (let i = 0; i < colIndex; i++) {
                                performance -= degradationFactor;
                              }
                              const degradationPerformance = Math.max(0, performance) / 100; // Convert to decimal
                              const annualElectricity = pvComputed.annualElectricity;
                              const operationalSavings = -(gi * degradationPerformance * annualElectricity);
                              return operationalSavings.toFixed(2);
                            })()
                          ) : rowIndex === 5 ? (
                            colIndex === 0 ? pvComputed.totalEmbodied.toLocaleString() : (() => {
                              // Calculate cumulative net value
                              let cumulativeNet = pvComputed.totalEmbodied; // Start with embodied carbon

                              for (let year = 1; year <= colIndex; year++) {
                                // Get GI for this year
                                const gi = useCustomGI ? (parseFloat(savedGIRow[year]) || 0) : (parseFloat(pvInputs.gridIntensity) || 0);

                                // Calculate degradation performance for this year
                                const degradationFactor = parseFloat(pvInputs.degradation) || 0;
                                let performance = 100;
                                for (let i = 0; i < year; i++) {
                                  performance -= degradationFactor;
                                }
                                const degradationPerformance = Math.max(0, performance) / 100;

                                // Calculate operational savings for this year
                                const annualElectricity = pvComputed.annualElectricity;
                                const operationalSavings = -(gi * degradationPerformance * annualElectricity);

                                // Add to cumulative net
                                cumulativeNet += operationalSavings;
                              }

                              return cumulativeNet.toFixed(1);
                            })()
                          ) : (
                            '-'
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* PV ROI Graph */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-gray-800">PV ROI Graph</h4>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadChartAsPDF}
              disabled={isDownloading || !hasRoiData}
              className="h-8 px-3 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <div className="w-3 h-3 mr-1 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
              ) : (
                <Download className="w-3 h-3 mr-1" />
              )}
              {isDownloading ? 'Generating...' : 'Download Chart'}
            </Button>
          </div>
          {(() => {
            if (!hasRoiData) {
              return (
                <div className="w-full rounded-lg border border-emerald-200 bg-white/60 p-6 text-center text-sm text-gray-600">
                  Compute the table to visualize the graph
                </div>
              );
            }
            const baseBarW = 20; // px per bar
            const gapW = 10; // px gap between bars
            const height = 260; // svg height
            const margin = { top: 24, right: 48, bottom: 40, left: 48 };
            const numYears = roiSeries.years.length; // dynamic width based on current years
            const innerW = numYears * baseBarW + Math.max(0, numYears - 1) * gapW;
            const width = innerW + margin.left + margin.right;
            const innerH = height - margin.top - margin.bottom;

            const yScale = d3
              .scaleLinear()
              .domain([roiSeries.minY, roiSeries.maxY === roiSeries.minY ? roiSeries.minY + 1 : roiSeries.maxY])
              .nice()
              .range([innerH, 0]);

            const zeroY = yScale(0);

            const barW = baseBarW;

            return (
              <div ref={chartContainerRef} className="w-full rounded-lg border border-emerald-200 bg-white/60 overflow-hidden p-3 md:p-4 relative">
                {/* PDF Generation Overlay - covers only the chart content area */}
                {isDownloading && (
                  <div data-pdf-overlay="true" className="absolute inset-0 bg-white/95 backdrop-blur-md z-40 flex flex-col items-center justify-center rounded-lg border-2 border-emerald-200 shadow-xl">
                    {/* Industrial Carbon Process Visualization */}
                    <div className="relative mb-8">
                      {/* Main Industrial Container */}
                      <div className="w-24 h-20 bg-gradient-to-b from-emerald-100 to-emerald-200 rounded-lg border-2 border-emerald-300 shadow-lg relative overflow-hidden">
                        {/* Carbon Emission Flow Lines */}
                        <div className="absolute inset-0">
                          {/* Horizontal flow lines */}
                          <div className="absolute top-2 left-0 w-full h-0.5 bg-emerald-400 animate-pulse"></div>
                          <div className="absolute top-4 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '0.3s'}}></div>
                          <div className="absolute top-6 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '0.6s'}}></div>
                          <div className="absolute top-8 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '0.9s'}}></div>
                          <div className="absolute top-10 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '1.2s'}}></div>
                        </div>
                        
                        {/* Vertical processing columns */}
                        <div className="absolute left-2 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse"></div>
                        <div className="absolute left-6 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                        <div className="absolute left-10 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.8s'}}></div>
                        <div className="absolute left-14 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '1.2s'}}></div>
                        <div className="absolute left-18 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '1.6s'}}></div>
                        <div className="absolute left-22 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
                        
                        {/* Carbon particles flowing through */}
                        <div className="absolute top-1 left-1 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0s'}}></div>
                        <div className="absolute top-3 left-5 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        <div className="absolute top-5 left-9 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                        <div className="absolute top-7 left-13 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.6s'}}></div>
                        <div className="absolute top-9 left-17 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.8s'}}></div>
                        <div className="absolute top-11 left-21 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '1s'}}></div>
                      </div>
                      
                      {/* Industrial Pipes and Connections */}
                      <div className="absolute -top-2 left-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
                      <div className="absolute -top-2 right-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
                      <div className="absolute -bottom-2 left-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
                      <div className="absolute -bottom-2 right-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{animationDelay: '1.5s'}}></div>
                      
                      {/* Connection Nodes */}
                      <div className="absolute -top-2 left-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping"></div>
                      <div className="absolute -top-2 right-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
                      <div className="absolute -bottom-2 left-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
                      <div className="absolute -bottom-2 right-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{animationDelay: '1.5s'}}></div>
                      
                      {/* Carbon Emission Clouds */}
                      <div className="absolute -top-4 left-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse"></div>
                      <div className="absolute -top-4 right-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{animationDelay: '0.7s'}}></div>
                      <div className="absolute -bottom-4 left-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{animationDelay: '1.4s'}}></div>
                      <div className="absolute -bottom-4 right-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{animationDelay: '2.1s'}}></div>
                    </div>
                    
                    {/* Industrial Process Text */}
                    <div className="text-xl font-semibold text-emerald-700 mb-3 animate-pulse">
                      Processing Carbon Data
                    </div>
                    
                    {/* Process Status Indicators */}
                    <div className="flex space-x-3 mb-4">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                        <div className="text-xs text-emerald-600 mt-1">Collecting</div>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{animationDelay: '0.3s'}}></div>
                        <div className="text-xs text-emerald-600 mt-1">Analyzing</div>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
                        <div className="text-xs text-emerald-600 mt-1">Compiling</div>
                      </div>
                    </div>
                    
                    {/* Industrial Subtitle */}
                    <div className="text-base text-emerald-600 text-center max-w-sm animate-pulse">
                      Well, wait for sometime untill I process the data...
                    </div>
                  </div>
                )}
                <div className="flex flex-col items-center">
                  <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} width={width} height={height} className="h-64" style={{ width: width }}>
                    <g transform={`translate(${margin.left},${margin.top})`}>
                      <defs>
                        <clipPath id="roiClip">
                          <rect x="0" y="0" width={innerW} height={innerH} />
                        </clipPath>
                      </defs>
                      {/* Y grid lines */}
                      {yScale.ticks(5).map((t, i) => (
                        <line key={i} x1={0} x2={innerW} y1={yScale(t)} y2={yScale(t)} stroke="#e5e7eb" strokeWidth={1} />
                      ))}

                      {/* X axis labels (years) */}
                      {roiSeries.years.map((yr, i) => (
                        <text key={yr} x={i * (barW + gapW) + barW / 2} y={innerH + 18} textAnchor="middle" className="fill-gray-600 text-[10px]">
                          {yr}
                        </text>
                      ))}

                      {/* Zero axis */}
                      <line x1={0} x2={innerW} y1={zeroY} y2={zeroY} stroke="#9ca3af" strokeWidth={1} />

                      {/* Clipped plot area for bars and line */}
                      <g clipPath="url(#roiClip)">
                        {/* Embodied carbon bar at year 0 (blue) */}
                        {roiSeries.embodied[0] > 0 && (
                          <rect
                            x={0}
                            width={barW}
                            y={yScale(roiSeries.embodied[0])}
                            height={Math.max(0, zeroY - yScale(roiSeries.embodied[0]))}
                            fill="#dc2626"
                          />
                        )}

                        {/* Operational savings bars from year 1 (yellow/orange) */}
                        {roiSeries.operationalSavings.map((val, i) => {
                          if (i === 0) return null;
                          const x = i * (barW + gapW);
                          const y = Math.min(zeroY, yScale(val));
                          const h = Math.abs(yScale(val) - zeroY);
                          return (
                            <rect
                              key={`s-${i}`}
                              x={x}
                              width={barW}
                              y={y}
                              height={h}
                              fill="#059669"
                              stroke="none"
                            />
                          );
                        })}

                        {/* Net line (grey) */}
                        {roiSeries.net.length > 1 && (() => {
                          const lineGen = d3
                            .line()
                            .x((d, i) => i * (barW + gapW) + barW / 2)
                            .y((d) => yScale(d));
                          const dAttr = lineGen(roiSeries.net) || '';
                          return (
                            <path
                              d={dAttr}
                              fill="none"
                              stroke="#374151"
                              strokeWidth={2}
                            />
                          );
                        })()}
                      </g>

                      {/* Y axis labels */}
                      {yScale.ticks(5).map((t, i) => (
                        <text key={`yt-${i}`} x={-8} y={yScale(t)} textAnchor="end" dominantBaseline="middle" className="fill-gray-600 text-[10px]">
                          {t}
                        </text>
                      ))}
                    </g>
                  </svg>
                  
                  {/* Graph Legend */}
                  <div className="mt-3 flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-red-600 rounded-sm"></div>
                      <span className="text-xs text-gray-600">Embodied Carbon</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-emerald-600 rounded-sm"></div>
                      <span className="text-xs text-gray-600">Operational Savings</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-4 h-0.5 bg-gray-600"></div>
                      <span className="text-xs text-gray-600">Net Carbon</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Break-even Analysis Summary */}
        <div className="mt-6 border border-gray-200 rounded-lg p-4 bg-gray-50/50">
          <h4 className="font-semibold text-gray-800 mb-4">Break-even Analysis</h4>
          <div className="border-b border-gray-300 w-full mb-4"></div>
          {(() => {
            if (!hasRoiData) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-semibold text-gray-700 mb-2">Break-even Point</h5>
                    <p className="text-sm text-gray-600">Enter PV system and context details to view results.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-semibold text-gray-700 mb-2">Summary</h5>
                    <p className="text-sm text-gray-600">Enter PV system and context details to view results.</p>
                  </div>
                </div>
              );
            }

            // Find break-even point (when net becomes 0 or negative)
            let breakEvenYear = null;
            let breakEvenYears = null;
            
            for (let i = 1; i < roiSeries.net.length; i++) {
              if (roiSeries.net[i] <= 0) {
                breakEvenYear = roiSeries.years[i];
                breakEvenYears = i;
                break;
              }
            }

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h5 className="font-semibold text-gray-700 mb-2">Break-even Point</h5>
                  {breakEvenYear ? (
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Year:</span> {breakEvenYear}
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Years to break-even:</span> {breakEvenYears} years
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Net carbon at break-even:</span> {roiSeries.net[breakEvenYears]?.toFixed(1) || '0.0'} kgCO2-eq
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Break-even not reached</span> within {pvInputs.pvServiceLife} years
                    </div>
                  )}
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h5 className="font-semibold text-gray-700 mb-2">Summary</h5>
                  {breakEvenYear ? (
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Initial embodied carbon:</span> {pvComputed.totalEmbodied.toLocaleString()} kgCO2-eq
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Final net carbon:</span> {roiSeries.net[roiSeries.net.length - 1]?.toFixed(1) || '0.0'} kgCO2-eq
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Total carbon savings:</span> {(() => {
                          const finalNet = roiSeries.net[roiSeries.net.length - 1] || 0;
                          const savings = finalNet < 0 ? Math.abs(finalNet) : (pvComputed.totalEmbodied - finalNet);
                          return savings.toFixed(1);
                        })()} kgCO2-eq
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600">
                        No carbon savings
                      </div>
                      <div className="text-sm font-bold text-justify text-gray-800">
                      Installing a PV system in this scenario is not advisable, as the break-even point is not achieved before the system reaches its end of life. Instead, consider low-carbon PV alternatives and verify whether the projected future grid intensity values are accurate. 
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </CardContent>
      
      <Dialog open={showGridLookup} onOpenChange={setShowGridLookup}>
        <DialogContent className="flex w-full max-h-[90vh] max-w-[95vw] flex-col overflow-hidden p-0 sm:w-auto md:max-w-[90vw] lg:max-w-[1200px]">
          <div className="px-4 pt-4 sm:px-6 sm:pt-6">
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-black">Grid carbon intensity lookup</DialogTitle>
              <DialogDescription className="text-black">Search Our World in Data for grid carbon intensity (gCO2/kWh) and apply the latest value to this analysis.</DialogDescription>
            </DialogHeader>
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-4 sm:px-6 sm:pb-6">
            <CarbonIntensityLookup onApplyValue={handleApplyLookupValue} className="mt-4" />
          </div>
        </DialogContent>
      </Dialog>

      {/* Validation Alert Popup */}
      {showValidationAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50 backdrop-blur-sm">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md shadow-sm p-4 max-w-sm mx-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-2 flex-1">
                {/* Caution symbol */}
                <svg className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="text-emerald-700 text-sm">
                  Enter the value between 25-35 years
                </div>
              </div>
              {/* Close button */}
              <button
                onClick={() => setShowValidationAlert(false)}
                className="ml-2 text-emerald-400 hover:text-emerald-600 transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default PVROIAnalysis;



